import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "components/ui/Modal";
import BasicButton from "components/forms/BasicButton";
import SelectInput from "components/forms/SelectInput";
import TextInput from "components/forms/TextInput";
import Checkbox from "components/forms/Checkbox";
import {
  getPricingImportTemplate,
  previewPricingImport,
  commitPricingImport,
  PreviewResult,
  ResolvedRow,
  RowOverride,
  CommitResult,
} from "api/ServicePricingImport";
import { getServices } from "api/Services";

/**
 * ServicePricingImportModal
 *
 * Generic bulk pricing importer UI. Mirrors ImportMapDedupModal's
 * preview -> per-row override -> commit shape. Flow:
 *   1. Download template (seeded from a reference service's pricing).
 *   2. Xander edits the CSV, uploads it here.
 *   3. Preview: per-row destination + status pill + skip/update on
 *      already-priced rows + a create-service confirmation callout.
 *   4. Commit: per-row results.
 *
 * Props let the caller preset the target/reference service (e.g. the Fibar
 * Install Only import). When targetService* are omitted, the CSV's own
 * service_id/service_name columns drive resolution.
 */

interface Props {
  /**
   * Reference service whose pricing seeds the template (e.g. Installation of
   * Mulch). When omitted, the modal opens on a picker so the user chooses the
   * reference + target services first (generic use).
   */
  referenceServiceId?: string | null;
  /** Target service to price. Omit id when it may not exist yet. */
  targetServiceId?: string | null;
  targetServiceName?: string | null;
  /** Heading shown in the modal. */
  title?: string;
  onClose: () => void;
  onCommitted?: (result: CommitResult) => void;
}

type Stage = "pick" | "upload" | "preview" | "committing" | "done";

interface ServiceLite {
  id: string;
  name: string;
}

const PILL: Record<string, { bg: string; fg: string; label: string }> = {
  ready: { bg: "#DCFCE7", fg: "#166534", label: "Ready" },
  will_create_combo: { bg: "#DBEAFE", fg: "#1E40AF", label: "Will create combo" },
  already_priced: { bg: "#FEF3C7", fg: "#92400E", label: "Already priced" },
  combo_not_found: { bg: "#FEE2E2", fg: "#991B1B", label: "Combo not found" },
  unresolved: { bg: "#FEE2E2", fg: "#991B1B", label: "Unresolved" },
  invalid_price: { bg: "#FEE2E2", fg: "#991B1B", label: "Invalid price" },
};

// Per-row commit outcome -> pill styling + human label.
const OUTCOME_PILL: Record<string, { bg: string; fg: string; label: string }> = {
  created_combo: { bg: "#DBEAFE", fg: "#1E40AF", label: "Combo created" },
  added_row: { bg: "#DCFCE7", fg: "#166534", label: "Added" },
  updated_row: { bg: "#DCFCE7", fg: "#166534", label: "Updated" },
  skipped: { bg: "#F3F4F6", fg: "#6B7280", label: "Skipped" },
  invalid: { bg: "#FEE2E2", fg: "#991B1B", label: "Invalid" },
  error: { bg: "#FEE2E2", fg: "#991B1B", label: "Failed" },
};

const styles: Record<string, React.CSSProperties> = {
  intro: { fontSize: 13, color: "#5D5E62", marginTop: 0, marginBottom: 16, lineHeight: 1.5 },
  toolbar: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 },
  dropArea: {
    border: "1px dashed #CBD5E1", borderRadius: 8, padding: "22px 16px", textAlign: "center",
    cursor: "pointer", background: "#F9FAFB", marginBottom: 12,
  },
  dropAreaLabel: { fontSize: 13, color: "#5D5E62" },
  fileName: { fontSize: 12, color: "#374151" },
  howBox: {
    fontSize: 12, color: "#374151", background: "#F3F4F6", border: "1px solid #E4E7EB",
    borderRadius: 8, padding: "12px 14px", marginBottom: 14, lineHeight: 1.6,
  },
  howTitle: { fontWeight: 600, color: "#111", marginBottom: 6 },
  howList: { margin: 0, paddingLeft: 18 },
  summaryRow: { display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: "#374151", margin: "8px 0 12px" },
  summaryChip: { padding: "4px 10px", background: "#F3F4F6", borderRadius: 6 },
  createCallout: {
    fontSize: 13, color: "#92400E", background: "#FFFBEB",
    border: "1px solid #FDE68A", borderRadius: 8, padding: "10px 12px", marginBottom: 12,
  },
  tableWrap: { maxHeight: 380, overflow: "auto", border: "1px solid #E4E7EB", borderRadius: 8 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: {
    position: "sticky", top: 0, background: "#F9FAFB", textAlign: "left",
    padding: "8px 10px", borderBottom: "1px solid #E4E7EB", color: "#374151", fontWeight: 600, whiteSpace: "nowrap",
  },
  td: { padding: "7px 10px", borderBottom: "1px solid #F1F3F5", color: "#111", verticalAlign: "top" },
  pill: { display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 },
  dest: { fontSize: 12, color: "#111" },
  destSub: { fontSize: 11, color: "#6B7280", marginTop: 1 },
  overrideSel: { fontSize: 12, padding: "2px 4px", border: "1px solid #D1D5DB", borderRadius: 4 },
  flags: { fontSize: 10, color: "#6B7280", marginTop: 2 },
  errText: { color: "#991B1B", fontSize: 12, marginTop: 8 },
  bulkRow: { flex: 1, display: "flex", gap: 8, alignItems: "center", fontSize: 12, color: "#5D5E62" },
  bulkBtn: { background: "transparent", border: "1px solid #E4E7EB", borderRadius: 4, padding: "3px 8px", fontSize: 11, cursor: "pointer", color: "#111" },
  doneHeadline: { fontSize: 15, fontWeight: 600, color: "#111", margin: "0 0 4px" },
  doneSub: { fontSize: 13, color: "#5D5E62", margin: "0 0 14px", lineHeight: 1.5 },
  doneBanner: {
    background: "#DCFCE7", border: "1px solid #A7F3D0", borderRadius: 8,
    padding: "12px 14px", marginBottom: 14,
  },
  doneBannerWarn: {
    background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8,
    padding: "12px 14px", marginBottom: 14,
  },
  outcomePill: { display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 },
};

const ServicePricingImportModal: React.FC<Props> = ({
  referenceServiceId: referenceServiceIdProp = null,
  targetServiceId: targetServiceIdProp = null,
  targetServiceName: targetServiceNameProp = null,
  title = "Import service pricing",
  onClose,
  onCommitted,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);

  // When the reference service is preset by the caller (e.g. the Fibar
  // Install Only import), skip the picker and go straight to upload.
  const presetReference = Boolean(referenceServiceIdProp);
  const [stage, setStage] = useState<Stage>(presetReference ? "upload" : "pick");

  // Reference + target become editable state so the picker can set them; they
  // seed from props for the preset case.
  const [referenceServiceId, setReferenceServiceId] = useState<string | null>(
    referenceServiceIdProp,
  );
  const [targetServiceId, setTargetServiceId] = useState<string | null>(
    targetServiceIdProp,
  );
  const [targetServiceName, setTargetServiceName] = useState<string | null>(
    targetServiceNameProp,
  );

  // Service list for the picker dropdowns (loaded only when picking).
  const [services, setServices] = useState<ServiceLite[]>([]);
  const [servicesLoaded, setServicesLoaded] = useState(false);

  const [csv, setCsv] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [importOverride, setImportOverride] = useState<RowOverride>("skip");
  const [rowOverrides, setRowOverrides] = useState<Record<number, RowOverride>>({});
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [commitResult, setCommitResult] = useState<CommitResult | null>(null);

  // Load services once, when the picker is shown.
  useEffect(() => {
    if (stage !== "pick" || servicesLoaded) return;
    let cancelled = false;
    (async () => {
      const res: any = await getServices({ query: { recordsPer: "500" } });
      if (cancelled) return;
      const items: ServiceLite[] = (res?.items ?? res ?? [])
        .filter((s: any) => s?.id && s?.name)
        .map((s: any) => ({ id: s.id, name: s.name }));
      setServices(items);
      setServicesLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [stage, servicesLoaded]);

  const downloadTemplate = async () => {
    setBusy(true);
    setError(null);
    const res = await getPricingImportTemplate({
      referenceServiceId,
      targetServiceId,
      targetServiceName,
    });
    setBusy(false);
    if ("error" in res) {
      setError(res.error);
      return;
    }
    const blob = new Blob([res.csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = res.filename || "pricing-import.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ""));
    reader.readAsText(file);
  };

  const runPreview = async (
    override: RowOverride = importOverride,
    overrides: Record<number, RowOverride> = rowOverrides,
  ) => {
    if (!csv.trim()) {
      setError("Upload a CSV first.");
      return;
    }
    setBusy(true);
    setError(null);
    const res = await previewPricingImport({
      csv,
      targetServiceId,
      targetServiceName,
      override,
      rowOverrides: overrides,
    });
    setBusy(false);
    if ("error" in res) {
      setError(res.error);
      return;
    }
    setPreview(res);
    setStage("preview");
  };

  const setRowOverride = (row: number, value: RowOverride) => {
    const next = { ...rowOverrides, [row]: value };
    setRowOverrides(next);
    runPreview(importOverride, next);
  };

  const setAllOverride = (value: RowOverride) => {
    setImportOverride(value);
    setRowOverrides({});
    runPreview(value, {});
  };

  const runCommit = async () => {
    if (!preview) return;
    if (preview.servicesToCreate.length > 0 && !confirmCreate) {
      setError("Confirm new-service creation before importing.");
      return;
    }
    setStage("committing");
    setBusy(true);
    setError(null);
    const res = await commitPricingImport({
      csv,
      targetServiceId,
      targetServiceName,
      override: importOverride,
      rowOverrides,
      confirmCreateService: confirmCreate,
    });
    setBusy(false);
    if ("error" in res) {
      setError(res.error);
      setStage("preview");
      return;
    }
    setCommitResult(res);
    setStage("done");
    onCommitted?.(res);
  };

  const committableCount = useMemo(() => {
    if (!preview) return 0;
    return preview.rows.filter(
      (r) =>
        r.primaryAction === "ADD_SERVICE_ROW" ||
        r.primaryAction === "CREATE_COMBO_AND_ROW" ||
        r.primaryAction === "UPDATE_ROW",
    ).length;
  }, [preview]);

  return (
    <Modal isOpen={true} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>{title}</ModalHeader>
      <ModalBody>
        {stage === "pick" && (
          <>
            <p style={styles.intro}>
              Choose the service to price and the reference service whose
              current pricing seeds the template. The target can be an existing
              service or a new one (typed below) that the import will create.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <SelectInput
                label="Reference service (seeds pricing)"
                required
                searchable
                options={services.map((s) => ({ value: s.id, label: s.name }))}
                value={referenceServiceId}
                onChange={(val: string) => setReferenceServiceId(val || null)}
              />

              <SelectInput
                label="Target service"
                description="Pick an existing service, or leave blank and type a new name below to create one."
                clearable
                searchable
                options={services.map((s) => ({ value: s.id, label: s.name }))}
                value={targetServiceId}
                onChange={(val: string) => {
                  const id = val || null;
                  setTargetServiceId(id);
                  const found = services.find((s) => s.id === id);
                  setTargetServiceName(found ? found.name : null);
                }}
              />

              <TextInput
                label="New target service name"
                placeholder="e.g. Install Only"
                description="Only used when no existing target service is selected above."
                disabled={Boolean(targetServiceId)}
                value={targetServiceId ? "" : (targetServiceName ?? "")}
                onChange={(val: string) => {
                  setTargetServiceId(null);
                  setTargetServiceName(val || null);
                }}
              />
            </div>
            {error && <div style={styles.errText}>{error}</div>}
          </>
        )}

        {stage === "upload" && (
          <>
            <p style={styles.intro}>
              Download the template, fill in the price columns, then upload it to
              preview every change before anything is saved.
            </p>
            <div style={styles.howBox}>
              <div style={styles.howTitle}>How this works</div>
              <ol style={styles.howList}>
                <li>Download the template. It has one row per depot that prices the reference service.</li>
                <li>Fill in <strong>import_lowCost</strong>, <strong>import_highCost</strong>, and <strong>import_markup</strong> for each depot you want to price. Leave the price cells blank to skip a row.</li>
                <li>Leave the other columns alone. <strong>combo_id</strong> and <strong>service_name</strong> tell the import where each price goes; the provider, depot, and current-price columns are there for reference only.</li>
                <li>Upload the file and review the preview. Existing pricing on each depot is never changed; this only adds the new service price.</li>
              </ol>
            </div>
            <div style={styles.toolbar}>
              <BasicButton type="button" scheme="secondary" outline keepText
                onClick={downloadTemplate} styles={{ margin: "0" }}>
                Download template
              </BasicButton>
            </div>
            <div
              style={styles.dropArea}
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                if (!file) return;
                setFileName(file.name);
                const reader = new FileReader();
                reader.onload = () => setCsv(String(reader.result || ""));
                reader.readAsText(file);
              }}
            >
              <span style={styles.dropAreaLabel}>
                {fileName ? fileName : "Drag and drop your CSV here, or click to choose a file"}
              </span>
            </div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} hidden />
            {error && <div style={styles.errText}>{error}</div>}
          </>
        )}

        {stage === "preview" && preview && (
          <>
            <div style={styles.summaryRow}>
              <span style={styles.summaryChip}>Total: {preview.summary.total}</span>
              <span style={styles.summaryChip}>Ready: {preview.summary.ready}</span>
              <span style={styles.summaryChip}>New combos: {preview.summary.willCreateCombo}</span>
              <span style={styles.summaryChip}>Already priced: {preview.summary.alreadyPriced}</span>
              <span style={styles.summaryChip}>Invalid: {preview.summary.invalid}</span>
            </div>

            {preview.servicesToCreate.length > 0 && (
              <div style={styles.createCallout}>
                <Checkbox
                  checked={confirmCreate}
                  onChange={(v: boolean) => setConfirmCreate(v)}
                  label={`This import will create a new service: ${preview.servicesToCreate.join(", ")}. Check to confirm.`}
                  labelColor="#92400E"
                />
              </div>
            )}

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>#</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Destination</th>
                    <th style={styles.th}>New price (L / H / M)</th>
                    <th style={styles.th}>If already priced</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r: ResolvedRow) => {
                    const pill = PILL[r.status] || PILL.unresolved;
                    const flags: string[] = [];
                    if (r.attachServiceToDepot) flags.push("attach to depot");
                    if (r.attachServiceToTemplate) flags.push("attach to template");
                    if (r.createService) flags.push("create service");
                    return (
                      <tr key={r.row}>
                        <td style={styles.td}>{r.row + 1}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.pill, background: pill.bg, color: pill.fg }}>
                            {pill.label}
                          </span>
                          {flags.length > 0 && <div style={styles.flags}>{flags.join(" · ")}</div>}
                        </td>
                        <td style={styles.td}>
                          <div style={styles.dest}>
                            {r.context.productName || "-"}
                            {r.context.depotName ? ` @ ${r.context.depotName}` : ""}
                          </div>
                          <div style={styles.destSub}>
                            {r.context.providerName || ""}
                            {r.context.serviceArea ? ` · ${r.context.serviceArea}` : ""}
                          </div>
                          {r.message && <div style={styles.destSub}>{r.message}</div>}
                        </td>
                        <td style={styles.td}>
                          {r.price
                            ? `${r.price.lowCost} / ${r.price.highCost} / ${r.price.markup}`
                            : "-"}
                        </td>
                        <td style={styles.td}>
                          {r.status === "already_priced" ? (
                            <select
                              style={styles.overrideSel}
                              value={rowOverrides[r.row] ?? importOverride}
                              onChange={(e) => setRowOverride(r.row, e.target.value as RowOverride)}
                            >
                              <option value="skip">Skip</option>
                              <option value="update">Update</option>
                            </select>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {error && <div style={styles.errText}>{error}</div>}
          </>
        )}

        {stage === "committing" && <p style={styles.intro}>Importing…</p>}

        {stage === "done" && commitResult && (() => {
            const s = commitResult.summary;
            const written = s.addedRows + s.updatedRows;
            const createdName =
              commitResult.createdServiceId ? (preview?.targetServiceName || targetServiceName) : null;
            // Join each commit result back to its preview row for readable
            // destination context (the commit payload only carries comboId).
            const rowCtx: Record<number, ResolvedRow> = {};
            (preview?.rows || []).forEach((r) => { rowCtx[r.row] = r; });

            // Plain-language headline.
            const parts: string[] = [];
            if (s.addedRows) parts.push(`${s.addedRows} price${s.addedRows === 1 ? "" : "s"} added`);
            if (s.updatedRows) parts.push(`${s.updatedRows} updated`);
            if (s.createdCombos) parts.push(`${s.createdCombos} combo${s.createdCombos === 1 ? "" : "s"} created`);
            if (s.skipped) parts.push(`${s.skipped} skipped`);
            if (s.failed) parts.push(`${s.failed} failed`);
            const headline =
              s.failed > 0
                ? "Import finished with some problems"
                : written > 0 || s.createdCombos > 0
                ? "Import complete"
                : "Nothing was imported";

            return (
              <>
                <p style={styles.doneHeadline}>{headline}</p>
                <p style={styles.doneSub}>
                  {parts.length ? parts.join(" \u00b7 ") : "No rows were eligible to import."}
                </p>

                <div style={s.failed > 0 ? styles.doneBannerWarn : styles.doneBanner}>
                  {createdName ? (
                    <div style={{ fontSize: 13, color: "#111", marginBottom: written ? 4 : 0 }}>
                      Created the service <strong>{createdName}</strong> and made it available on the depots below.
                    </div>
                  ) : null}
                  {written > 0 ? (
                    <div style={{ fontSize: 13, color: "#111" }}>
                      {createdName ? "Its price" : "Pricing"} is now live on {written} depot{written === 1 ? "" : "s"}. You can see it under Instant Estimate items on each depot.
                    </div>
                  ) : null}
                  {written === 0 && !createdName ? (
                    <div style={{ fontSize: 13, color: "#111" }}>
                      No pricing was written. Review the rows below for why.
                    </div>
                  ) : null}
                </div>

                <div style={styles.tableWrap}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>#</th>
                        <th style={styles.th}>Result</th>
                        <th style={styles.th}>Destination</th>
                        <th style={styles.th}>Price (L / H / M)</th>
                        <th style={styles.th}>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commitResult.results.map((res) => {
                        const ctx = rowCtx[res.row];
                        const pill = OUTCOME_PILL[res.outcome] || OUTCOME_PILL.error;
                        return (
                          <tr key={res.row}>
                            <td style={styles.td}>{res.row + 1}</td>
                            <td style={styles.td}>
                              <span style={{ ...styles.outcomePill, background: pill.bg, color: pill.fg }}>
                                {pill.label}
                              </span>
                            </td>
                            <td style={styles.td}>
                              <div style={styles.dest}>
                                {ctx?.context.productName || "-"}
                                {ctx?.context.depotName ? ` @ ${ctx.context.depotName}` : ""}
                              </div>
                              <div style={styles.destSub}>
                                {ctx?.context.providerName || ""}
                                {ctx?.context.serviceArea ? ` \u00b7 ${ctx.context.serviceArea}` : ""}
                              </div>
                            </td>
                            <td style={styles.td}>
                              {ctx?.price
                                ? `${ctx.price.lowCost} / ${ctx.price.highCost} / ${ctx.price.markup}`
                                : "-"}
                            </td>
                            <td style={styles.td}>{res.message || ""}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
      </ModalBody>
      <ModalFooter>
        {stage === "preview" && (
          <div style={styles.bulkRow}>
            <span>Already-priced rows:</span>
            <button type="button" style={styles.bulkBtn} onClick={() => setAllOverride("skip")}>Skip all</button>
            <button type="button" style={styles.bulkBtn} onClick={() => setAllOverride("update")}>Update all</button>
          </div>
        )}

        <BasicButton type="button" scheme="secondary" outline keepText
          onClick={onClose} styles={{ margin: "0" }}>
          {stage === "done" ? "Close" : "Cancel"}
        </BasicButton>

        {stage === "pick" && (
          <BasicButton type="button" keepText
            disabled={busy || !referenceServiceId || (!targetServiceId && !(targetServiceName && targetServiceName.trim()))}
            onClick={() => { setError(null); setStage("upload"); }} styles={{ margin: "0" }}>
            Continue
          </BasicButton>
        )}
        {stage === "upload" && (
          <BasicButton type="button" keepText disabled={busy || !csv.trim()}
            onClick={() => runPreview()} styles={{ margin: "0" }}>
            Preview
          </BasicButton>
        )}
        {stage === "preview" && (
          <BasicButton type="button" keepText disabled={busy || committableCount === 0}
            onClick={runCommit} styles={{ margin: "0" }}>
            Import {committableCount} row{committableCount === 1 ? "" : "s"}
          </BasicButton>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default ServicePricingImportModal;
